import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumb } from '../components/Breadcrumb';
import { CustomerSalesSection } from '../components/quoteToCash/CustomerSalesSection';
import { QuoteForecastSection } from '../components/quoteToCash/QuoteForecastSection';
import {
  SectionCard,
  SectionState,
} from '../components/quoteToCash/SectionCard';
import { YtdSection } from '../components/quoteToCash/YtdSection';
import { apiService } from '../services/apiService';
import {
  QuoteToCashView,
  YtdSection as YtdData,
} from '../services/quoteToCashTypes';

/**
 * Loads one section, again whenever `load` changes (e.g. with the view) or on retry. A reload keeps the previous
 * data on screen until the new data arrives.
 */
function useSection<T>(load: () => Promise<T>) {
  const [state, setState] = useState<SectionState<T>>({
    data: null,
    loading: true,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    load().then(
      (data) => active && setState({ data, loading: false, error: null }),
      (error: Error) =>
        active && setState({ data: null, loading: false, error: error.message })
    );
    return () => {
      active = false;
    };
  }, [load, attempt]);

  return { state, retry: () => setAttempt((a) => a + 1) };
}

/** Nothing this fiscal year or last, as on a new year's first morning with no history */
const isEmptyYtd = (data: YtdData) =>
  data.series.every((p) => !p.currentFY && !p.previousFY);

/** Quote-to-Cash dashboard: quotes and forecast, YTD sales, sales by customer, YTD invoicing */
export const QuoteToCashPage: React.FC = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<QuoteToCashView>('month');

  const forecast = useSection(
    useCallback(() => apiService.getQuoteForecast(view), [view])
  );
  const sales = useSection(
    useCallback(() => apiService.getYtdSales(view), [view])
  );
  const customers = useSection(
    useCallback(() => apiService.getCustomerSales(), [])
  );
  const invoicing = useSection(
    useCallback(() => apiService.getYtdInvoicing(view), [view])
  );
  const fy = sales.state.data?.currentFiscalYear;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 w-full">
      <div className="w-full">
        <Breadcrumb currentPage={t('navigation.quoteToCash')} />

        <div className="mb-6 mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('quoteToCash.title')}
            </h1>
            <p className="mt-2 text-gray-600">
              {fy
                ? t('quoteToCash.subtitleYear', { year: fy })
                : t('quoteToCash.subtitle')}
            </p>
          </div>
          <div
            className="inline-flex rounded-md border border-gray-300 bg-white p-0.5"
            role="group"
            aria-label={t('quoteToCash.viewLabel')}
          >
            {(['month', 'week'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={view === option}
                onClick={() => setView(option)}
                className={`px-4 py-1.5 text-sm font-medium rounded ${
                  view === option
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t(`quoteToCash.view.${option}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <SectionCard
            title={t('quoteToCash.forecast.title')}
            subtitle={t(`quoteToCash.forecast.subtitle.${view}`)}
            state={forecast.state}
            onRetry={forecast.retry}
            isEmpty={(data) =>
              data.series.every((p) => !p.quoteActual && !p.orderActual)
            }
          >
            {(data) => <QuoteForecastSection data={data} />}
          </SectionCard>

          <SectionCard
            title={t('quoteToCash.ytd.sales.title')}
            subtitle={t('quoteToCash.ytd.sales.subtitle')}
            state={sales.state}
            onRetry={sales.retry}
            isEmpty={isEmptyYtd}
          >
            {(data) => <YtdSection data={data} kind="sales" />}
          </SectionCard>

          <SectionCard
            title={t('quoteToCash.customers.title')}
            subtitle={t('quoteToCash.customers.subtitle')}
            state={customers.state}
            onRetry={customers.retry}
            isEmpty={(data) => data.customers.length === 0}
          >
            {(data) => <CustomerSalesSection data={data} />}
          </SectionCard>

          <SectionCard
            title={t('quoteToCash.ytd.invoicing.title')}
            subtitle={t('quoteToCash.ytd.invoicing.subtitle')}
            state={invoicing.state}
            onRetry={invoicing.retry}
            isEmpty={isEmptyYtd}
          >
            {(data) => <YtdSection data={data} kind="invoicing" />}
          </SectionCard>
        </div>
      </div>
    </div>
  );
};
